import React, { useState } from 'react';
import { Share2, X, Copy, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const ShareButton = ({ providerName, providerId, providerSlug }) => {
  const [showModal, setShowModal] = useState(false);
  
  // Generate share URL
  const shareUrl = `${window.location.origin}/dienstleister/${providerId}`;
  const shareTitle = `EventConnect – ${providerName}`;
  const shareText = `Schau dir ${providerName} auf EventConnect an!`;

  const handleShare = async () => {
    // Try native Web Share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        toast.success('Erfolgreich geteilt');
      } catch (error) {
        // User cancelled or error
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to modal
          setShowModal(true);
        }
      }
    } else {
      // Desktop: Show modal
      setShowModal(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link kopiert!');
      setShowModal(false);
    } catch (error) {
      console.error('Error copying:', error);
      toast.error('Fehler beim Kopieren');
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowModal(false);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`${shareText} ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowModal(false);
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(shareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
    setShowModal(false);
  };

  return (
    <>
      <button
        onClick={handleShare}
        className="w-12 h-12 shrink-0 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        title="Profil teilen"
        aria-label="Profil teilen"
      >
        <Share2 className="w-5 h-5" />
      </button>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Profil teilen</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* URL Display + Copy */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link zum Profil
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 focus:outline-none"
                  />
                  <button
                    onClick={copyLink}
                    className="px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Kopieren
                  </button>
                </div>
              </div>

              {/* Share Options */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Teilen über:</p>
                
                <button
                  onClick={shareViaEmail}
                  className="w-full flex items-center gap-3 p-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-[#FFF0D1] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#2B2142]" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">Per E-Mail</div>
                    <div className="text-sm text-gray-500">Link per E-Mail versenden</div>
                  </div>
                </button>

                <button
                  onClick={shareViaWhatsApp}
                  className="w-full flex items-center gap-3 p-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">WhatsApp</div>
                    <div className="text-sm text-gray-500">Über WhatsApp teilen</div>
                  </div>
                </button>

                <button
                  onClick={shareViaFacebook}
                  className="w-full flex items-center gap-3 p-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-[#FFF0D1] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2B2142]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">Facebook</div>
                    <div className="text-sm text-gray-500">Auf Facebook teilen</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;
